import React, { useState } from 'react';

import { MemoryChange, MemoryItem } from './MemoryItem';
import {
  MemoryList,
  MemoryScope,
  bannerProposals,
  replacedText,
} from './memoryApi';
import './memory.css';

interface Props {
  tenant: string;
  list: MemoryList | null;
  /** Only proposals in this scope (`bannerProposals`). Unset offers every one. */
  scope?: MemoryScope;
  onChanged: (change: MemoryChange) => void;
}

/** How many proposals are drawn before the banner asks to be opened. */
const SHOWN = 2;

/**
 * Proposals waiting for a decision, above the conversation.
 *
 * Nothing the agent or a session review suggests counts until a person approves it, so
 * the place to ask is where that person next turns up. Drawn only for someone who can
 * decide them (`bannerProposals`); everyone else sees the chat as it was.
 */
export function MemoryBanner({ tenant, list, scope, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const proposals = bannerProposals(list, scope);
  if (proposals.length === 0) return null;
  const hidden = proposals.length - SHOWN;
  return (
    <section
      className="mem-banner"
      aria-label="Suggested for memory"
      data-testid="memory-banner"
    >
      <h6 className="mem-banner-title">
        Suggested for memory
        <span className="mem-count">{proposals.length}</span>
      </h6>
      <ul className="mem-list">
        {(open ? proposals : proposals.slice(0, SHOWN)).map((entry) => (
          <MemoryItem
            key={entry.entry_id}
            tenant={tenant}
            entry={entry}
            canEdit
            replaces={replacedText(entry, list?.entries ?? [])}
            onChanged={onChanged}
          />
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          className="mem-more"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? 'Show fewer' : `Show all ${proposals.length}`}
        </button>
      )}
    </section>
  );
}
